
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import EmailHtmlFrame from '@/components/helpdesk/EmailHtmlFrame.vue';
import EmailAttachments from '@/components/helpdesk/EmailAttachments.vue';
import {IEmailAttachment} from '@/services/helpdeskEmailAttachments';
import SignatureSelector from '@/components/helpdesk/SignatureSelector.vue';
import HelpdeskLinkedUsersTab from '@/components/helpdesk/HelpdeskLinkedUsersTab.vue';
import HelpdeskAssigneePicker from '@/components/helpdesk/HelpdeskAssigneePicker.vue';
import HelpdeskRelatedEmailsTab from '@/components/helpdesk/HelpdeskRelatedEmailsTab.vue';
import {IHelpdeskTicket, ITicketSentEmail} from '@/store/modules/helpdeskTicketsModule';
import {ITicketType} from '@/store/modules/helpdeskTicketTypesModule';
import {IEmailTemplate} from '@/store/modules/helpdeskEmailTemplatesModule';
import {inboxEmailRoute, ticketRelatedEmails} from '@/helpers/helpdeskTicketEmails';
import {ticketStatusLabel, ticketStatusOptions, ticketStatusShortLabel} from '@/helpers/helpdeskTicketStatus';

interface ITimelineItem {
  _type: 'received' | 'sent';
  id: number;
  date: string;
  from_email?: string;
  to_email?: string;
  sent_by?: string | null;
  subject: string;
  body: string;
  cc_emails?: string[] | null;
  bcc_emails?: string[] | null;
  attachments?: IEmailAttachment[] | null;
  unread?: boolean;
}

@Component({name: 'HelpdeskTicketModal', components: {HelpdeskAssigneePicker, EmailHtmlFrame, EmailAttachments, RichTextEditor, SignatureSelector, HelpdeskLinkedUsersTab, HelpdeskRelatedEmailsTab}})
export default class HelpdeskTicketModal extends Vue {
  @Prop({required: true}) open!: boolean;
  @Prop({required: true}) ticketId!: number;

  internalOpen      = this.open;
  // Per timeline item (timelineKey): long emails start clamped and can be expanded.
  expanded: {[key: string]: boolean} = {};
  clamped:  {[key: string]: boolean} = {};
  inboxEmailRoute   = inboxEmailRoute;
  activeTab         = 0;
  valid             = false;
  saving            = false;
  loadingTypes      = false;
  loadingTemplates  = false;
  sendingTemplate   = false;
  confirmDelete     = false;
  templateReplyOpen = false;
  templateReplyId: number | null = null;
  linkedCount       = 0;

  private subscribes: any = {};
  private pendingTemplateId: number | null = null;
  private signatureBody = '';

  form = {
    subject:             '',
    status:              'open',
    ticket_type_id:      null as number | null,
    reporter_email:      '',
    assignee_email:      '',
    description:         '',
    autoReplyTemplateId: null as number | null,
  };

  get statusOptions() {
    return ticketStatusOptions(this.$i18n);
  }

  @Watch('open')
  onOpenChanged(val: boolean) {
    this.internalOpen = val;
    if (val) {
      this.activeTab = 0;
      this.linkedCount = 0;
      this.loadTicket();
      this.loadTypes();
      this.loadAllTemplates();
    }
  }

  @Watch('internalOpen')
  onInternalChanged(val: boolean) {
    if (!val) this.$emit('update:open', false);
  }

  get ticket(): IHelpdeskTicket | null {
    return this.$store.getters['helpdeskTicketsModule/currentTicket'];
  }

  get ticketTypes(): ITicketType[] {
    return this.$store.getters['helpdeskTicketTypesModule/types'];
  }

  get allTemplates(): IEmailTemplate[] {
    return this.$store.getters['helpdeskEmailTemplatesModule/allTemplates'];
  }

  get selectedTemplateName(): string {
    if (!this.form.autoReplyTemplateId) return '';
    const t = this.allTemplates.find(x => x.id === this.form.autoReplyTemplateId);
    return t ? t.name : '';
  }

  get templateReplyPreview(): string {
    if (!this.templateReplyId) return '';
    const t = this.allTemplates.find(x => x.id === this.templateReplyId);
    return t ? t.body : '';
  }

  get currentUserEmail(): string {
    const userData = this.$store.getters['authModule/userData'];
    return (userData && userData.email) ? userData.email : '';
  }

  get senderEmails(): string[] {
    return ticketRelatedEmails(this.ticket);
  }

  get emailCount(): number {
    if (!this.ticket) return 0;
    const rx = (this.ticket.emails || []).length;
    const tx = (this.ticket.sent_emails || []).length;
    return rx + tx;
  }

  timelineKey(item: ITimelineItem): string {
    return item._type + '-' + item.id;
  }

  get timeline(): ITimelineItem[] {
    if (!this.ticket) return [];
    const items: ITimelineItem[] = [];

    (this.ticket.emails || []).forEach((e: any) => {
      items.push({
        _type:     'received',
        id:        e.id,
        date:      e.received_at,
        from_email: e.from_email,
        to_email:  e.to_email,
        subject:   e.subject,
        body:      e.body,
        attachments: e.attachments,
        unread:    e.status === 'pending',
      });
    });

    (this.ticket.sent_emails || []).forEach((s: ITicketSentEmail) => {
      items.push({
        _type:      'sent',
        id:         s.id,
        date:       s.sent_at,
        to_email:   s.to_email,
        sent_by:    s.sent_by,
        subject:    s.subject,
        body:       s.body,
        cc_emails:  s.cc_emails,
        bcc_emails: s.bcc_emails,
      });
    });

    items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return items;
  }

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.ticketLoaded': () => {
        if (this.internalOpen) {
          this.syncFormFromTicket();
          // Opening the ticket reads its new replies; the timeline still marks them as new.
          if (this.ticket && this.ticket.unread_emails_count) {
            this.$store.dispatch('helpdeskTicketsModule/markTicketEmailsAsRead', this.ticket.id);
          }
        }
      },
      'backend.call.OK.ticketUpdated': () => {
        this.saving = false;
        // If template reply pending, send it after update
        if (this.pendingTemplateId && this.ticket) {
          this.dispatchTemplateReply(this.ticket.id, this.pendingTemplateId, undefined, this.currentUserEmail);
          this.pendingTemplateId = null;
        }
        this.$emit('saved');
      },
      'backend.call.KO.ticketUpdated': () => {
        this.saving = false;
        this.pendingTemplateId = null;
      },
      'backend.call.OK.ticketDeleted': () => {
        this.$emit('deleted');
        this.internalOpen = false;
      },
      'backend.call.OK.templateReplySent': () => {
        this.sendingTemplate     = false;
        this.templateReplyOpen   = false;
        this.templateReplyId     = null;
        if (this.ticket) {
          this.loadTicket();
        }
      },
      'backend.call.KO.templateReplySent': () => {
        this.sendingTemplate = false;
      },
    });

    if (this.open) {
      this.loadTicket();
      this.loadTypes();
      this.loadAllTemplates();
    }
  }

  beforeDestroy() { (this as any).$offs(this.subscribes); }

  private loadTicket() {
    this.$store.dispatch('helpdeskTicketsModule/fetchTicket', this.ticketId);
  }

  private loadTypes() {
    this.loadingTypes = true;
    this.$store.dispatch('helpdeskTicketTypesModule/fetchTypes', {all: true})
      .then(() => { this.loadingTypes = false; })
      .catch(() => { this.loadingTypes = false; });
  }

  private loadAllTemplates() {
    this.loadingTemplates = true;
    this.$store.dispatch('helpdeskEmailTemplatesModule/fetchAllTemplates')
      .then(() => { this.loadingTemplates = false; })
      .catch(() => { this.loadingTemplates = false; });
  }

  private syncFormFromTicket() {
    const t = this.ticket;
    if (!t) return;
    this.form = {
      subject:             t.subject,
      status:              t.status,
      ticket_type_id:      t.ticket_type_id,
      reporter_email:      t.reporter_email || '',
      assignee_email:      t.assignee_email || '',
      description:         t.description || '',
      autoReplyTemplateId: null,
    };
  }

  private onSave() {
    const formRef = this.$refs.form as any;
    if (!formRef || !formRef.validate()) return;
    if (!this.ticket) return;
    this.saving = true;
    this.pendingTemplateId = this.form.autoReplyTemplateId;

    if (this.form.autoReplyTemplateId && this.ticket) {
      this.pendingTemplateId = this.form.autoReplyTemplateId;
    }

    this.$store.dispatch('helpdeskTicketsModule/updateTicket', {
      id:   this.ticket.id,
      data: {
        subject:        this.form.subject,
        status:         this.form.status,
        ticket_type_id: this.form.ticket_type_id || undefined,
        reporter_email: this.form.reporter_email || undefined,
        assignee_email: this.form.assignee_email || null,
        description:    this.form.description || undefined,
      },
    });
  }

  private onDelete() {
    this.confirmDelete = true;
  }

  private doDelete() {
    if (!this.ticket) return;
    this.confirmDelete = false;
    this.$store.dispatch('helpdeskTicketsModule/deleteTicket', this.ticket.id);
  }

  private openTemplateReply() {
    this.templateReplyId   = null;
    this.signatureBody     = '';
    this.templateReplyOpen = true;
  }

  private doSendTemplateReply() {
    if (!this.templateReplyId || !this.ticket) return;
    this.sendingTemplate = true;
    this.dispatchTemplateReply(this.ticket.id, this.templateReplyId, this.signatureBody || undefined, this.currentUserEmail);
  }

  private dispatchTemplateReply(ticketId: number, templateId: number, signatureBody?: string, sentBy?: string) {
    this.$store.dispatch('helpdeskTicketsModule/sendTemplateReply', {ticketId, templateId, signatureBody, sentBy});
  }

  close() {
    if (this.saving) return;
    this.internalOpen = false;
  }

  private statusLabel(status: string): string {
    return ticketStatusLabel(this.$i18n, status);
  }

  private statusShortLabel(status: string): string {
    return ticketStatusShortLabel(this.$i18n, status);
  }


  private shortDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'}) +
      ' ' + d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }
}
